import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { getNextDocketNo } from "../services/docketEntryService";
import CustomDropdown from "./CustomDropdown";
import { destinationOptions } from "../utils/helper";

const CHARGE_KEYS = [
  "baseFreight",
  "processingCharge",
  "fuelSurcharge",
  "rovCharge",
  "fodCharge",
  "handlingCharge",
  "codDodCharge",
  "specialDelivery",
  "charges",
  "otherCharges",
];

const DocketForm = ({ formData, setFormData }) => {
  const docketMode = formData?.docketNoMode || "auto"; // "auto" | "manual"

  const handleGenerateDocketNo = async () => {
    try {
      const res = await getNextDocketNo();
      setFormData({ ...formData, docketNumber: res?.data?.docketNumber });
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate Docket No");
    }
  };

  // Auto-calculate SUB-TOTAL (sum of charges) and GRAND TOTAL (sub-total + GST)
  useEffect(() => {
    const c = formData?.charges || {};
    const sum = CHARGE_KEYS.reduce((t, k) => t + (parseFloat(c[k]) || 0), 0);
    const subTotal = sum ? sum.toFixed(2) : "";
    const gstVal = sum * 0.18;
    const gst = sum ? gstVal.toFixed(2) : "";
    const grandTotal = sum ? String(Math.round(sum + gstVal)) : "";

    if (
      subTotal !== (c.subTotal ?? "") ||
      gst !== (c.gst ?? "") ||
      grandTotal !== (c.grandTotal ?? "")
    ) {
      setFormData({
        ...formData,
        charges: { ...c, subTotal, gst, grandTotal },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.charges]);

  return (
    <div>
      <div className="bg-white w-full md:w-auto  mx-auto text-xs rounded-xl shadow-lg">
        {/* TOP BOOKING ROW */}
        <div className="grid grid-cols-4">
          {/* Booking Date */}
          <div className="p-3 border-r">
            <div className="font-semibold mb-1">
              Origin<span className="text-red-500">*</span>
            </div>
            <input
              type="text"
              className="w-full text-sm border border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={formData?.origin}
              onChange={(e) =>
                setFormData({ ...formData, origin: e.target.value })
              }
            />
          </div>

          {/* Destination */}
          <div className="p-3 border-r">
            <CustomDropdown
              label="Destination"
              required
              value={formData.destination}
              options={destinationOptions}
              onChange={(val) => setFormData({ ...formData, destination: val })}
            />
          </div>

          {/* Transport Mode */}
          <div className="p-3 border-r">
            <div className="font-semibold mb-1">Transport Mode</div>
            <select
              className="w-full text-sm border border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={formData?.transportMode}
              onChange={(e) =>
                setFormData({ ...formData, transportMode: e.target.value })
              }
            >
              <option value="">Select Mode</option>
              <option value="Surface">Surface</option>
              <option value="Air">Air</option>
              {/* <option value="Surface / Air">Surface / Air</option> */}
            </select>
          </div>

          {/* Docket Number */}
          <div className="p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold">
                Docket Number<span className="text-red-500">*</span>
              </span>
              {/* compact Auto / Manual switch */}
              <div
                role="tablist"
                className="inline-flex p-0.5 rounded-md bg-slate-100 border border-slate-200"
              >
                {[
                  ["auto", "Auto"],
                  ["manual", "Manual"],
                ].map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={docketMode === m}
                    onClick={() => {
                      if (docketMode === m) return;
                      setFormData({
                        ...formData,
                        docketNoMode: m,
                        docketNumber: "",
                      });
                    }}
                    className={`px-2 py-0.5 rounded text-[11px] font-medium transition ${
                      docketMode === m
                        ? "bg-white text-sky-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder={
                  docketMode === "auto" ? "Click Generate" : "OPSS000001"
                }
                readOnly={docketMode === "auto"}
                className={`w-full text-sm border border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500 ${
        docketMode === "auto" ? "bg-gray-100" : ""
      }`}
                value={formData?.docketNumber || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    docketNumber: e.target.value.toUpperCase(),
                  })
                }
              />
              {docketMode === "auto" && (
                <button
                  type="button"
                  onClick={handleGenerateDocketNo}
                  className="px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded text-xs font-medium transition"
                >
                  Generate
                </button>
              )}
            </div>
          </div>
        </div>

        {/* MAIN CONTENT GRID */}
        <div className="grid grid-cols-3 ">
          {/* LEFT SIDE (FROM + TO + BOXES) */}
          <div className="col-span-2 border-r ">
            {/* FROM + TO */}
            <div className="grid grid-cols-2">
              {/* FROM */}
              <div className="border-r border-gray-300">
                <div className="bg-slate-200 text-black font-bold px-3 py-2 text-center">
                  FROM (Consignor)
                </div>

                <div className="p-3 space-y-3">
                  {/* Name */}
                  <div>
                    <div className="font-semibold mb-1">
                      Name<span className="text-red-500">*</span>
                    </div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.from?.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          from: { ...formData.from, name: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <div className="font-semibold mb-1">
                      Address<span className="text-red-500">*</span>
                    </div>
                    <textarea
                      rows={2}
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.from?.address}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          from: { ...formData.from, address: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Phone No */}
                  <div>
                    <div className="font-semibold mb-1">
                      Phone No<span className="text-red-500">*</span>
                    </div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.from?.phoneNo}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          from: { ...formData.from, phoneNo: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* GST */}
                  <div>
                    <div className="font-semibold mb-1">GST</div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.from?.gst}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          from: { ...formData.from, gst: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Bottom Row */}
                  <div className="flex justify-between items-center">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 focus:outline-none focus:ring-1 focus:ring-sky-500"
                        checked={formData?.from?.dropOff}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            from: {
                              ...formData.from,
                              dropOff: e.target.checked,
                            },
                          })
                        }
                      />
                      <span className="font-semibold">Drop Off</span>
                    </label>

                    <div className="text-sky-600 cursor-pointer p-1 border rounded border-sky-600">
                      + Add
                    </div>
                  </div>
                </div>
              </div>

              {/* TO */}
              <div>
                <div className="bg-slate-200 text-black font-bold px-3 py-2 text-center">
                  TO (Consignee)
                </div>

                <div className="p-3 space-y-3">
                  {/* Name */}
                  <div>
                    <div className="font-semibold mb-1">
                      Name<span className="text-red-500">*</span>
                    </div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.to?.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          to: { ...formData.to, name: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Address */}
                  <div>
                    <div className="font-semibold mb-1">
                      Address<span className="text-red-500">*</span>
                    </div>
                    <textarea
                      rows={2}
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.to?.address}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          to: { ...formData.to, address: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Phone No */}
                  <div>
                    <div className="font-semibold mb-1">
                      Phone No<span className="text-red-500">*</span>
                    </div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.to?.phoneNo}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          to: { ...formData.to, phoneNo: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* GST */}
                  <div>
                    <div className="font-semibold mb-1">GST</div>
                    <input
                      type="text"
                      className="w-full border text-sm border-gray-300 px-2 py-2 rounded
      focus:outline-none focus:ring-1 focus:ring-sky-500"
                      value={formData?.to?.gst}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          to: { ...formData.to, gst: e.target.value },
                        })
                      }
                    />
                  </div>

                  {/* Self Collect */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      checked={formData?.to?.selfCollect}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          to: {
                            ...formData.to,
                            selfCollect: e.target.checked,
                          },
                        })
                      }
                    />
                    <span className="font-semibold">Self Collect</span>
                  </label>
                </div>
              </div>
            </div>

            {/* MERGED ROW: NO OF BOXES + PACKAGE INFO */}
            <div className="grid grid-cols-2 border-t border-b">
              <div className="border-r">
                <div className="bg-slate-200 text-black font-bold px-4 py-2">
                  No. of Boxes<span className="text-red-500">*</span>
                </div>
                <div className="p-4">
                  <input
                    type="number"
                    className="w-full border border-gray-300 px-2 py-2 rounded"
                    value={formData?.numberOfBoxes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        numberOfBoxes: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <div className="bg-slate-200 text-black font-bold px-4 py-2">
                  Package Information
                </div>
                <div className="p-4">
                  <input
                    type="text"
                    className="w-full border border-gray-300 px-2 py-2 rounded"
                    value={formData?.packageInfo}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        packageInfo: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 px-4 pt-4">
              <div>
                <div className="font-semibold mb-1">Invoice No</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  type="text"
                  value={formData?.invoiceNo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      invoiceNo: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Invoice Date</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  type="date"
                  value={formData?.invoiceDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      invoiceDate: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Invoice Value</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  value={formData?.invoiceValue}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      invoiceValue: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">E-Way Bill No</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  value={formData?.eWayBillNo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      eWayBillNo: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 p-4">
              <div>
                <div className="font-semibold mb-1">
                  Boxes X Dimension(cm)<span className="text-red-500">*</span>
                </div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  type="text"
                  value={formData?.boxDimension}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      boxDimension: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Package Type</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  value={formData?.packageType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      packageType: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Said to Contain</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  value={formData?.saidToContain}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      saidToContain: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold mb-1">Risk Coverage</div>
                <select
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  value={formData?.riskCoverage}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      riskCoverage: e.target.value,
                    })
                  }
                >
                  <option value="">Select</option>
                  <option value="Yes">Carrieris Risk</option>
                  <option value="No">Owneris Risk</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 px-4 pb-4">
              <div>
                <div className="font-semibold mb-1">Policy & Validity</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  type="text"
                  value={formData?.policyValidity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      policyValidity: e.target.value,
                    })
                  }
                />
              </div>
              <div className="border p-2 rounded">
                <div className="flex justify-between ">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={formData?.paymentMode?.cash}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentMode: {
                            ...formData.paymentMode,
                            cash: e.target.checked,
                          },
                        })
                      }
                    />
                    <div className="font-semibold">Cash</div>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={formData?.paymentMode?.dd}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentMode: {
                            ...formData.paymentMode,
                            dd: e.target.checked,
                          },
                        })
                      }
                    />
                    <div className="font-semibold">DD</div>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={formData?.paymentMode?.cheque}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentMode: {
                            ...formData.paymentMode,
                            cheque: e.target.checked,
                          },
                        })
                      }
                    />
                    <div className="font-semibold ">Cheque</div>
                  </label>
                </div>
                <div className="pt-2">
                  <div className="font-semibold mb-1">Amount in Words</div>
                  <input
                    className="w-full border border-gray-300 px-2 py-2 rounded"
                    type="text"
                    value={formData?.amountInWords}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        amountInWords: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <div>
                  <div className="font-semibold mb-1">COD Amount</div>
                  <input
                    className="w-full border border-gray-300 px-2 py-2 rounded"
                    type="number"
                    value={formData?.codAmount}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        codAmount: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 px-4 pb-4">
              <div>
                <div className="font-semibold mb-1"># of Documents</div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded"
                  type="number"
                  value={formData?.numberOfDocuments}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numberOfDocuments: e.target.value,
                    })
                  }
                />
              </div>
              <div className="border p-2 rounded">
                <div className="font-semibold">Documents Received</div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <input
                      className=" border border-gray-300 h-4 w-4 rounded"
                      type="checkbox"
                      checked={formData?.documentsReceived?.invoice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          documentsReceived: {
                            ...formData.documentsReceived,
                            invoice: e.target.checked,
                          },
                        })
                      }
                    />
                    <div>Invoice</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      className=" border border-gray-300 h-4 w-4 rounded"
                      type="checkbox"
                      checked={formData?.documentsReceived?.eWaybill}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          documentsReceived: {
                            ...formData.documentsReceived,
                            eWaybill: e.target.checked,
                          },
                        })
                      }
                    />
                    <div>E-waybill</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      className=" border border-gray-300 h-4 w-4 rounded"
                      type="checkbox"
                      checked={formData?.documentsReceived?.others}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          documentsReceived: {
                            ...formData.documentsReceived,
                            others: e.target.checked,
                          },
                        })
                      }
                    />
                    <div>Others</div>
                  </div>
                </div>
              </div>
              <div className="border p-2 rounded">
                <div className="font-semibold mb-1">Value Added Service</div>
                <div className="flex flex-col  justify-between py-2">
                  <div className="flex items-center gap-1">
                    <input
                      className=" border border-gray-300 h-4 w-4 rounded"
                      type="checkbox"
                      checked={formData?.valueAddedService?.appointmentDelivery}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          valueAddedService: {
                            ...formData.valueAddedService,
                            appointmentDelivery: e.target.checked,
                          },
                        })
                      }
                    />
                    <div>Appointment Delivery</div>
                  </div>
                  <div className="flex items-center gap-1 pt-2">
                    <input
                      className=" border border-gray-300 h-4 w-4 rounded"
                      type="checkbox"
                      checked={formData?.valueAddedService?.specialDelivery}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          valueAddedService: {
                            ...formData.valueAddedService,
                            specialDelivery: e.target.checked,
                          },
                        })
                      }
                    />
                    <div>Special Delivery</div>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="grid grid-cols-3 gap-2 pb-4 border-b">
              <div>
                <div className="font-semibold px-4 pb-2">
                  Pick up Agent Name
                </div>
                <input
                  className="w-full border border-gray-300 px-2 py-2 rounded mx-4 mb-4"
                  value={formData?.pickUpAgentName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pickUpAgentName: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <div className="font-semibold px-4 pb-2">Sign</div>
                <input className="w-full border border-gray-300 px-2 py-2 rounded mx-4 mb-4" />
              </div>
            </div> */}
            <div className="grid grid-cols-3 gap-2 pb-36 mt-2">
              <div>
                <div className="font-semibold px-4 text-center pb-2">
                  Consignee stamp
                </div>
              </div>
              <div>
                <div className="font-semibold px-4 text-center pb-2">
                  Name & Signature
                </div>
              </div>
              <div>
                <div className="font-semibold px-4 text-center pb-2">
                  Date & Time
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE (CHARGES PANEL) */}
          <div>
            {/* Weights */}
            <div className="grid grid-cols-2 border-b ">
              <div className="bg-slate-200 text-black font-bold text-center p-2 border-r">
                Actual Wt (Kg)<span className="text-red-500">*</span>
              </div>
              <div className="bg-slate-200 text-black font-bold text-center p-2">
                Charged Wt (Kg)<span className="text-red-500">*</span>
              </div>
              <input
                className="border-r p-2 outline-none text-sm"
                value={formData?.actualWeight}
                onChange={(e) =>
                  setFormData({ ...formData, actualWeight: e.target.value })
                }
              />
              <input
                className="p-2 outline-none text-sm"
                value={formData?.chargedWeight}
                onChange={(e) =>
                  setFormData({ ...formData, chargedWeight: e.target.value })
                }
              />
            </div>

            {/* Payment Type */}
            <div className="border-b ">
              <div className="bg-slate-200 text-black font-bold text-center py-2">
                Payment Type<span className="text-red-500">*</span>
              </div>
              <div className="flex justify-around p-2">
                {/* {["CREDIT", "PAID", "FOD"].map((p) => ( */}
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.paymentType?.credit}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paymentType: {
                          ...formData.paymentType,
                          credit: e.target.checked,
                        },
                      })
                    }
                  />
                  CREDIT
                </label>

                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.paymentType?.paid}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paymentType: {
                          ...formData.paymentType,
                          paid: e.target.checked,
                        },
                      })
                    }
                  />
                  PAID
                </label>

                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.paymentType?.fod}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        paymentType: {
                          ...formData.paymentType,
                          fod: e.target.checked,
                        },
                      })
                    }
                  />
                  FOD
                </label>

                {/* ))} */}
              </div>
            </div>

            {/* Charges */}
            <div className="border-b ">
              <div className="bg-slate-200 text-black font-bold text-center py-2">
                Charges (Retail Pick-Ups)
              </div>

              {/* Base Freight */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Base Freight</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.baseFreight}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        baseFreight: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Processing Charge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">
                  Processing Charge
                </div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.processingCharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        processingCharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Fuel Surcharge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Fuel Surcharge</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.fuelSurcharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        fuelSurcharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* ROV Charge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">ROV Charge</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.rovCharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        rovCharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* FOD Charge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">FOD Charge</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.fodCharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        fodCharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Handling Charge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Handling Charge</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.handlingCharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        handlingCharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* COD/DOD Charge */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">COD/DOD Charge</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.codDodCharge}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        codDodCharge: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Special Delivery */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Special Delivery</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.specialDelivery}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        specialDelivery: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Charges */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Charges</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.charges}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        charges: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* Other Charges */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 border-r font-medium">Other Charges</div>
                <input
                  className="p-2 outline-none text-sm"
                  value={formData?.charges?.otherCharges}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      charges: {
                        ...formData.charges,
                        otherCharges: e.target.value,
                      },
                    })
                  }
                />
              </div>

              {/* SUB-TOTAL */}
              <div className="grid grid-cols-2 border-b">
                <div className="p-2 font-bold border-r bg-slate-200 text-black">
                  SUB-TOTAL
                </div>
                <input
                  className="p-2 outline-none text-sm bg-gray-50 font-semibold"
                  value={formData?.charges?.subTotal ?? ""}
                  readOnly
                />
              </div>

              {/* GST */}
              <div className="grid grid-cols-2">
                <div className="p-2 font-bold border-r">GST (18%)</div>
                <input
                  className="p-2 outline-none text-sm bg-gray-50 font-semibold"
                  value={formData?.charges?.gst ?? ""}
                  readOnly
                />
              </div>

              {/* GRAND TOTAL */}
              <div className="grid grid-cols-2 border-t border-b border-gray-100">
                <div className="p-2 font-bold border-r bg-slate-200 text-black">
                  GRAND TOTAL (≈ Rounded)
                </div>
                <input
                  className="p-2 outline-none text-sm bg-gray-50 font-semibold"
                  value={formData?.charges?.grandTotal ?? ""}
                  readOnly
                />
              </div>
            </div>

            {/* Mode of Payment */}
            <div className=" ">
              <div className="bg-slate-200 text-black font-bold text-center py-2">
                MODE OF PAYMENT
              </div>
              <div className="flex flex-wrap gap-3 justify-between p-2">
                {/* Cash */}
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.modeOfPayment?.cash}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        modeOfPayment: {
                          ...formData.modeOfPayment,
                          cash: e.target.checked,
                        },
                      })
                    }
                  />
                  <span>Cash</span>
                </label>

                {/* Cheque / DD */}
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.modeOfPayment?.chequeDd}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        modeOfPayment: {
                          ...formData.modeOfPayment,
                          chequeDd: e.target.checked,
                        },
                      })
                    }
                  />
                  <span>Cheque/DD</span>
                </label>

                {/* NEFT / RTGS */}
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={formData?.modeOfPayment?.neftRtgs}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        modeOfPayment: {
                          ...formData.modeOfPayment,
                          neftRtgs: e.target.checked,
                        },
                      })
                    }
                  />
                  <span>NEFT/RTGS</span>
                </label>

                <span className="text-[10px]">(Only POP)</span>
              </div>
            </div>

            <div className="p-2 border-b  font-semibold">
              Cheque is favour of Odisha Parcel Service Pvt. Ltd.
            </div>

            {/* Cheques / NEFT No. */}
            <div className="border-b">
              <div className="p-2 font-semibold">Cheques / NEFT No.</div>
              <input
                className="p-2 w-full outline-none border-t text-sm"
                value={formData?.chequeNeftNo}
                onChange={(e) =>
                  setFormData({ ...formData, chequeNeftNo: e.target.value })
                }
              />
            </div>

            {/* Bank */}
            <div className="border-b">
              <div className="p-2 font-semibold">Bank</div>
              <input
                className="p-2 w-full outline-none border-t text-sm"
                value={formData?.bank}
                onChange={(e) =>
                  setFormData({ ...formData, bank: e.target.value })
                }
              />
            </div>

            {/* Amount (in words) */}
            <div className="border-b">
              <div className="p-2 font-semibold">Amount (in words)</div>
              <input
                className="p-2 w-full outline-none border-t text-sm"
                value={formData?.amountInWordsPayment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amountInWordsPayment: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocketForm;
